import { useState } from 'react';
import { TeamState, LostReason } from '@/types';
import { ScoreBar } from '@/components/ScoreBar';

interface PointWinnerModalProps {
  team: TeamState;
  lostReason: LostReason;
  onSelect: (playerId: string) => Promise<void>;
  onCancel: () => void;
  scoreTeamA?: TeamState;
  scoreTeamB?: TeamState;
  scoreA?: number;
  scoreB?: number;
  servingTeamId?: string;
}

export default function PointWinnerModal({
  team,
  onSelect,
  onCancel,
  scoreTeamA,
  scoreTeamB,
  scoreA,
  scoreB,
  servingTeamId,
}: PointWinnerModalProps) {
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const promptText = `Select a Point Winner`;

  const handleSelect = async (playerId: string) => {
    setSelectingId(playerId);
    setError(null);

    try {
      await onSelect(playerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Selection failed');
    } finally {
      setSelectingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50">
      <div className="absolute bottom-0 left-0 right-0 bg-surface border-t border-border rounded-t-2xl p-4 pb-safe max-h-[80vh] overflow-y-auto animate-slide-up">
        <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />

        {scoreTeamA && scoreTeamB && scoreA !== undefined && scoreB !== undefined && (
          <ScoreBar teamA={scoreTeamA} teamB={scoreTeamB} scoreA={scoreA} scoreB={scoreB} servingTeamId={servingTeamId} />
        )}

        <h2 className="text-lg font-bold text-text-primary text-center mb-3">
          {promptText.toUpperCase()}
        </h2>

        {/* Team Header */}
        <div
          className="rounded-lg p-3 mb-4"
          style={{ backgroundColor: `${team.color}15`, borderColor: team.color, borderWidth: 1 }}
        >
          <p className="font-semibold text-center" style={{ color: team.color }}>
            {team.name.toUpperCase()}
          </p>
        </div>

        {/* Player Grid - Dark Themed Cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {team.players.map((player) => {
            const isSelected = selectingId === player.id;
            return (
              <button
                key={player.id}
                onClick={() => handleSelect(player.id)}
                disabled={selectingId !== null}
                className="py-5 px-3 rounded-lg font-bold text-xl transition-all active:scale-[0.98]"
                style={{
                  backgroundColor: isSelected ? team.color : 'rgb(39, 39, 42)',
                  color: '#ffffff',
                  border: `4px solid ${team.color}`,
                  boxShadow: isSelected ? `0 4px 12px ${team.color}60` : undefined,
                }}
              >
                {isSelected ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-white rounded-full animate-spin" style={{ borderTopColor: 'transparent' }} />
                    {player.name}
                  </span>
                ) : (
                  player.name
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="bg-error/20 border border-error rounded-lg p-3 mb-4">
            <p className="text-error text-sm text-center">{error}</p>
          </div>
        )}

        <button onClick={onCancel} className="btn-secondary text-text-secondary">
          BACK
        </button>
      </div>
    </div>
  );
}
